import { FC, useState } from 'react';

import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { EventHooks, useFormat, useModalState, ValuationHooks } from '../../../../../hooks/index';
import {
  EventExercisedTypeBackend,
  EventTypeBackend,
  ExerciseEventItem,
} from '../../../../../types/events.types';
import { SharePlanType } from '../../../../../types/pool-plans.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { FilesModal } from '../../../modals/index';
import { resolveExerciseTransactionFilesLinks } from '../../../utils/resolve-event-document-links.util';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { EventSignatureStatusTableCell, Td } from './table-td';

export const Payout: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();
  const { valuation } = ValuationHooks.useCurrent();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);

  const { items, totalPages, isLoading } = EventHooks.useTransactions<ExerciseEventItem>({
    eventId: props.eventId,
    eventType: EventTypeBackend.PAYOUT,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  const renderFilesCell = (item: ExerciseEventItem, eventFilesLinks: string[]) => (
    <EventTransactionFilesCell
      date={item.date}
      docusignFileLinks={item.docusignFileLinks}
      filesLinks={resolveExerciseTransactionFilesLinks(item, eventFilesLinks)}
      linkedSignatureFileLink={item.linkedSignatureFileLink}
      linkedSignatureId={item.linkedSignatureId}
      onDocumentClick={handleDocumentClick}
      onOpenFilesModal={handleOpenFilesModalForRow}
      signatureStatus={item.signatureStatus}
      stakeholderName={item.vestingTask.grantItem.stakeholder.fullName}
    />
  );

  if (props.type !== EventTypeBackend.PAYOUT) return <></>;

  const planType = items[0]?.vestingTask?.grantItem?.plan?.type;

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={props.name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      {planType === SharePlanType.PHANTOM ? (
        <BasicTable
          eventType={EventExercisedTypeBackend.PHANTOM}
          transactionView={props.transactionView}
          {...tableStateProps}
        >
          {items.map((item) => {
            const {
              id,
              exercised,
              vestingTask,
              payout,
              date,
              signatureSignedCount,
              signatureStatus,
              signatureTotalCount,
              exerciseEvent: {
                event: { filesLinks },
              },
            } = item;

            return (
              <tr key={id}>
                <Td
                  value={
                    <Tooltip content={vestingTask.grantItem.stakeholder.fullName}>
                      <span>{vestingTask.grantItem.stakeholder.fullName}</span>
                    </Tooltip>
                  }
                />
                <Td className="uppercase" value={vestingTask.grantItem.id.split('-')[0]} />
                <Td value={format(date, 'dd/MM/yyyy')} />
                <Td value={toNumber(payout && payout / exercised).toLocaleString('en-US')} />
                <Td value={exercised?.toLocaleString('en-US')} />
                <Td value={payout && payout?.toLocaleString('en-US')} />
                <EventSignatureStatusTableCell
                  signatureSignedCount={signatureSignedCount}
                  signatureStatus={signatureStatus}
                  signatureTotalCount={signatureTotalCount}
                />
                {!props.transactionView && <Td value={renderFilesCell(item, filesLinks ?? [])} />}
              </tr>
            );
          })}
        </BasicTable>
      ) : (
        <BasicTable
          eventType={EventTypeBackend.PAYOUT}
          transactionView={props.transactionView}
          {...tableStateProps}
        >
          {items.map((item) => {
            const {
              id,
              exercised,
              vestingTask,
              date,
              signatureSignedCount,
              signatureStatus,
              signatureTotalCount,
              exerciseEvent: {
                event: { filesLinks },
              },
            } = item;

            return (
              <tr key={id}>
                <Td value={format(date, 'dd/MM/yyyy')} />
                <Td value={<AppFormattedMessage id={'PAYOUT'} />} />
                <Td
                  value={
                    <Tooltip content={vestingTask.grantItem.stakeholder.fullName}>
                      <span>{vestingTask.grantItem.stakeholder.fullName}</span>
                    </Tooltip>
                  }
                />
                <Td value={exercised.toLocaleString('en-US')} />
                <Td
                  value={`${(exercised * toNumber(valuation?.sharePrice)).toLocaleString('en-US')}`}
                />
                <Td value={vestingTask.grantItem.numbersOfGrants.toLocaleString('en-US')} />
                <EventSignatureStatusTableCell
                  signatureSignedCount={signatureSignedCount}
                  signatureStatus={signatureStatus}
                  signatureTotalCount={signatureTotalCount}
                />
                {!props.transactionView && <Td value={renderFilesCell(item, filesLinks ?? [])} />}
              </tr>
            );
          })}
        </BasicTable>
      )}
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
